/**
 * Product Linking Manager
 * Ensures parent product is in cart before adding bundle/assembly addons
 *
 * Handles three cases:
 * 1. Product not in cart → Show variant picker
 * 2. Single instance in cart → Auto-link to that item
 * 3. Multiple instances in cart → Let user choose which to link to (or add new)
 *
 * Usage:
 * const manager = new ProductLinkingManager({
 *   productId: 12345,
 *   productHandle: 'kids-electric-car',
 *   onReady: (productLinkId, cartItem) => { ... },
 *   onCancel: () => { ... },
 *   onError: (error) => { ... }
 * });
 * manager.ensureParentInCart();
 */

const DEBUG_PLM = false; // Set to true for development debugging

if (!window.ProductLinkingManager) {
  window.ProductLinkingManager = class ProductLinkingManager {
    constructor(options) {
      this.productId = options.productId;
      this.productHandle = options.productHandle;
      this.currentVariantId = options.productVariantId || null;

      // Callbacks
      this.onReady = options.onReady || (() => {});
      this.onCancel = options.onCancel || (() => {});
      this.onError = options.onError || ((error) => console.error('[PLM] Error:', error));

      // State
      this.productData = null;
      this.cartData = null;
      this.selectedVariantId = this.currentVariantId;
      this.selectedNewVariantId = this.currentVariantId; // For "Add New" expansion
      this.modal = null;

      // Logging removed
    }

    /**
     * Main entry point - ensures parent product is in cart
     */
    async ensureParentInCart() {
      try {
        // Logging removed

        // Step 1: Check parent product stock FIRST
        const stockCheck = await this.checkParentStock();

        if (!stockCheck.available) {
          console.warn('[PLM] Parent product out of stock:', stockCheck.message);
          const error = new Error('Parent product is out of stock. Cannot add addon items.');
          error.stockUnavailable = true;
          throw error;
        }

        // Logging removed

        // Step 2: Fetch cart state
        this.cartData = await this.fetchCart();
        // Logging removed

        // Step 3: Find existing cart items for this product
        const cartItems = this.findProductInCart();
        // Logging removed

        // Step 4: Route to appropriate flow
        // ALWAYS use combined modal with "Add a New Option"
        if (cartItems.length === 0) {
          // Case 1: Not in cart → Show combined modal with ONLY "Add New" option
          await this.handleNoCartItem();
        } else if (cartItems.length === 1) {
          // Case 2: Single instance → Show combined modal with existing + "Add New"
          await this.handleMultipleCartItems([cartItems[0]]);
        } else {
          // Case 3: Multiple instances → Show combined modal
          await this.handleMultipleCartItems(cartItems);
        }

      } catch (error) {
        console.error('[PLM] Error in ensureParentInCart:', error);
        this.onError(error);
      }
    }

    /**
     * Fetch current cart state
     */
    async fetchCart() {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    }

    /**
     * Find all cart items matching this product
     */
    findProductInCart() {
      // Logging removed
      // Logging removed

      const matches = this.cartData.items.filter(item =>
        item.product_id === this.productId
      );

      // Logging removed
      return matches;
    }

    /**
     * Case 1: Product not in cart - show combined modal with only "Add New" option
     */
    async handleNoCartItem() {
      // Logging removed

      // Fetch product data to get variants
      await this.fetchProductData();

      // Show combined modal with empty cart items (only "Add New" will show)
      await this.showCartItemSelectorModal([]);
    }

    /**
     * Case 2: Single instance in cart - auto-link
     */
    async handleSingleCartItem(cartItem) {
      // Logging removed

      // Get or generate productLinkId
      const productLinkId = await this.ensureProductLinkId(cartItem);

      // Logging removed

      // Trigger success callback
      this.onReady(productLinkId, cartItem);
    }

    /**
     * Case 3: Multiple instances in cart - show selector
     */
    async handleMultipleCartItems(cartItems) {
      // Logging removed

      // Fetch product data for variant details
      await this.fetchProductData();

      // Show cart item selector modal
      await this.showCartItemSelectorModal(cartItems);
    }

    /**
     * Ensure cart item has productLinkId property
     */
    async ensureProductLinkId(cartItem) {
      // Check if already has productLinkId
      if (cartItem.properties?.productLinkId) {
        return cartItem.properties.productLinkId;
      }

      // Generate new productLinkId
      const productLinkId = `${this.productId}-${Date.now()}`;

      // Logging removed

      // Update cart item with productLinkId
      await this.updateCartItemProperties(cartItem, { productLinkId });

      return productLinkId;
    }

    /**
     * Update cart item properties
     */
    async updateCartItemProperties(cartItem, newProperties) {
      const lineIndex = this.cartData.items.indexOf(cartItem) + 1; // 1-indexed

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          properties: {
            ...cartItem.properties,
            ...newProperties
          }
        })
      });

      if (!response.ok) throw new Error('Failed to update cart item');

      const updatedCart = await response.json();

      // Refresh cart drawer after property update
      await this.refreshCartDrawer();

      return updatedCart;
    }

    /**
     * Refresh cart drawer with sections update (matches bundle drawer pattern)
     */
    async refreshCartDrawer() {
      try {
        const sectionsToUpdate = [];

        // Get cart drawer section ID
        const cartDrawerElement = document.querySelector('cart-drawer');
        if (cartDrawerElement) {
          sectionsToUpdate.push(window.theme.utils.sectionId(cartDrawerElement));
        }

        // Get cart page section ID (if on cart page)
        const cartPageElement = document.querySelector('.cart-page');
        if (cartPageElement) {
          sectionsToUpdate.push(window.theme.utils.sectionId(cartPageElement));
        }

        if (sectionsToUpdate.length === 0) {
          console.warn('[PLM] No cart sections found to update');
          return;
        }

        // Logging removed

        // Fetch updated sections
        const response = await fetch(window.theme.routes.cart_update_url, {
          ...window.theme.utils.fetchConfig(),
          method: 'POST',
          body: JSON.stringify({
            sections: sectionsToUpdate
          })
        });

        const updatedState = await response.json();
        // Logging removed

        // Publish cart update event
        if (window.theme && window.theme.pubsub) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            cart: updatedState
          });
          // Logging removed
        }

      } catch (error) {
        console.error('[PLM] Error refreshing cart drawer:', error);
      }
    }

    /**
     * Get tier attributes for a new addon being added to the bundle
     * Returns the tier info BEFORE adding the item (for setting on the new item)
     */
    async getTierAttributesForNewAddon(productLinkId, newAddonPrice) {
      // Logging removed

      // Fetch current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Find existing addon items with this productLinkId
      const existingAddons = cart.items.filter(item =>
        item.properties &&
        item.properties.productLinkId === productLinkId &&
        item.properties._bundleComponent === 'true'
      );

      // Logging removed

      // Calculate total spend INCLUDING the new addon
      const existingSpend = existingAddons.reduce((sum, item) => {
        return sum + ((item.price / 100) * item.quantity);
      }, 0);

      const totalSpend = existingSpend + newAddonPrice;
      const totalCount = existingAddons.length + 1; // +1 for the new addon

      // Logging removed
      // Logging removed
      // Logging removed
      // Logging removed

      // Calculate tier based on total spend
      let tier = 'basic';
      if (totalSpend >= 250) {
        tier = 'elite-bundle';  // 30% discount
      } else if (totalSpend >= 150) {
        tier = 'complete-bundle';  // 25% discount
      } else if (totalSpend >= 50) {
        tier = 'value-bundle';  // 15% discount
      }

      // Logging removed

      return {
        _bundleTier: tier,
        _bundle: tier,
        _bundleItemCount: String(totalCount)
      };
    }

    /**
     * Recalculate and update bundle tier for all items with the same productLinkId
     * This ensures discounts are applied correctly after adding addons
     */
    async recalculateBundleTier(productLinkId) {
      // Logging removed

      // Fetch fresh cart data
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      // Logging removed

      // Find all items with this productLinkId
      const linkedItems = cart.items.filter(item =>
        item.properties && item.properties.productLinkId === productLinkId
      );

      // Logging removed

      if (linkedItems.length === 0) {
        console.warn('[PLM-TIER] No linked items found - aborting tier calculation');
        return;
      }

      // Logging removed

      // Calculate total addon spend (exclude the parent product)
      const addonItems = linkedItems.filter(item =>
        item.properties._bundleComponent === 'true'
      );

      // Logging removed

      const totalAddonSpend = addonItems.reduce((sum, item) => {
        const itemTotal = (item.price / 100) * item.quantity;
        // Logging removed
        return sum + itemTotal;
      }, 0);

      // Logging removed

      // Calculate tier based on spend (MUST match bundle-shopify-adapter.js calculateTier() logic)
      // SPEND-BASED TIERS:
      // - £50-£149: 15% off (value-bundle)
      // - £150-£249: 25% off (complete-bundle)
      // - £250+: 30% off (elite-bundle)
      let bundleTier = 'basic';
      if (totalAddonSpend >= 250) {
        bundleTier = 'elite-bundle';  // 30% discount
      } else if (totalAddonSpend >= 150) {
        bundleTier = 'complete-bundle';  // 25% discount
      } else if (totalAddonSpend >= 50) {
        bundleTier = 'value-bundle';  // 15% discount
      }

      // Logging removed

      // Calculate bundle item count (CRITICAL for Shopify Function discount validation)
      // IMPORTANT: _bundleItemCount = number of ADDON items only (NOT including parent product)
      // The Shopify Function only groups items with _bundleComponent='true' (addons only)
      const bundleItemCount = String(addonItems.length);
      // Logging removed

      // Update tier attributes ONLY on addon items (items with _bundleComponent='true')
      // IMPORTANT: Parent product should ONLY have productLinkId, nothing else!
      // - Parent product: Only productLinkId (no tier attributes, no discount)
      // - Addon items: _bundleTier, _bundle, _bundleItemCount, _bundleComponent='true'
      const updatePromises = addonItems.map(async (item, index) => {
        const lineIndex = cart.items.indexOf(item) + 1; // 1-indexed

        // Logging removed
        // Logging removed
        // Logging removed

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineIndex,
            properties: {
              ...item.properties,
              _bundleTier: bundleTier,
              _bundle: bundleTier,
              _bundleItemCount: bundleItemCount
              // _bundleComponent is already 'true' from when addon was added
            }
          })
        });

        const result = await response.json();
        // Logging removed

        return result;
      });

      const results = await Promise.all(updatePromises);

      // Logging removed

      // Fetch cart again to verify the updates
      const verifyResponse = await fetch('/cart.js');
      const verifyCart = await verifyResponse.json();
      const verifyAddonItems = verifyCart.items.filter(item =>
        item.properties &&
        item.properties.productLinkId === productLinkId &&
        item.properties._bundleComponent === 'true'
      );

      // Logging removed
      // Logging removed
      // Logging removed

      // Verify parent product has ONLY productLinkId
      const verifyParent = verifyCart.items.find(item =>
        item.properties &&
        item.properties.productLinkId === productLinkId &&
        !item.properties._bundleComponent
      );

      if (verifyParent) {
        // Logging removed
        // Logging removed
      }

      // Logging removed
      // Logging removed
    }

    /**
     * Fetch product data from Shopify API
     */
    async fetchProductData() {
      if (this.productData) return this.productData;

      const response = await fetch(`/products/${this.productHandle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product data');

      this.productData = await response.json();

      // Set default variant if not set
      if (!this.selectedVariantId && this.productData.variants.length > 0) {
        this.selectedVariantId = this.productData.variants[0].id;
      }

      return this.productData;
    }

    /**
     * Check if parent product has stock available
     * Validates that parent product has at least one variant in stock
     * @returns {{available: boolean, message: string}}
     */
    async checkParentStock() {
      // Fetch product data if not already loaded
      await this.fetchProductData();

      if (!this.productData || !this.productData.variants) {
        console.warn('[PLM] No product data available for stock check');
        return {
          available: true, // Don't block if data not available
          message: 'No product data available'
        };
      }

      const { variants, title } = this.productData;

      // Check if ANY variant has stock available
      const hasStockInAnyVariant = variants.some(variant => {
        // Check if variant is available
        if (variant.available === true) {
          return true;
        }

        // Check inventory policy - if 'continue', can sell when out of stock
        if (variant.inventory_policy === 'continue') {
          return true;
        }

        // Check if inventory management is disabled
        if (!variant.inventory_management || variant.inventory_management === null) {
          return true;
        }

        // Check if variant has inventory quantity > 0
        if (variant.inventory_quantity && variant.inventory_quantity > 0) {
          return true;
        }

        return false;
      });

      if (hasStockInAnyVariant) {
        // Logging removed
        return {
          available: true,
          message: `Parent product "${title}" has stock available`
        };
      } else {
        // Logging removed
        return {
          available: false,
          message: `Parent product "${title}" is out of stock`
        };
      }
    }

    /**
     * Show variant picker modal
     */
    async showVariantPickerModal() {
      // Logging removed

      // Build modal HTML
      const modalHTML = this.buildVariantPickerHTML();

      // Inject into DOM
      this.injectModal(modalHTML, 'product-linking-variant-picker');

      // Attach event listeners
      this.attachVariantPickerListeners();

      // Show modal
      this.showModal();
    }

    /**
     * Show cart item selector modal
     */
    async showCartItemSelectorModal(cartItems) {
      // Logging removed

      // Build modal HTML
      const modalHTML = this.buildCartItemSelectorHTML(cartItems);

      // Inject into DOM
      this.injectModal(modalHTML, 'product-linking-cart-selector');

      // Attach event listeners
      this.attachCartItemSelectorListeners(cartItems);

      // Show modal
      this.showModal();

      // Auto-select "Add a New Product" section after modal is shown
      setTimeout(() => {
        const newCard = this.modal.querySelector('.cart-item-card--new');
        if (newCard) {
          // Deselect all existing cart items
          this.modal.querySelectorAll('.cart-item-card:not(.cart-item-card--new)').forEach(card => {
            card.classList.remove('selected');
            const checkbox = card.querySelector('.cart-item-card__checkbox');
            if (checkbox) checkbox.innerHTML = '';
          });

          // Select "Add New" card
          newCard.classList.add('selected');
          const checkbox = newCard.querySelector('.cart-item-card__checkbox');
          if (checkbox) {
            checkbox.innerHTML = '<span class="cart-item-card__checkbox-icon">✓</span>';
          }

          // Ensure expanded state
          const expandable = newCard.querySelector('.cart-item-card__expandable');
          const chevron = newCard.querySelector('.cart-item-card__chevron');
          if (expandable && chevron) {
            expandable.style.display = 'block';
            chevron.style.transform = 'rotate(180deg)';
          }

          // Logging removed
        }
      }, 300); // Wait for modal animation to complete
    }

    /**
     * Build variant picker modal HTML
     */
    buildVariantPickerHTML() {
      const product = this.productData;
      const currentVariant = product.variants.find(v => v.id === this.selectedVariantId) || product.variants[0];

      return `
        <x-modal id="product-linking-variant-picker"
                 class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none"
                 role="dialog"
                 aria-modal="true"
                 aria-label="Choose Your Options"
                 hidden>

          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none"
                           aria-controls="product-linking-variant-picker"
                           aria-expanded="false"></overlay-element>

          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">

            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <div>
                <h3 class="drawer__title heading font-bold title-md mb-1">Choose Your Options First</h3>
                <p class="text-sm opacity-70">Select your preferences before adding extras</p>
              </div>
              <button
                class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center"
                type="button"
                is="hover-button"
                data-action="close"
                aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>

            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink p-5 md:p-12">

                <!-- Product Preview -->
                <div class="text-center">
                  <img src="${currentVariant.featured_image?.src || product.featured_image}"
                       alt="${product.title}"
                       class="product-preview__image mx-auto"
                       style="width: 100%; max-width: 200px; height: auto; border-radius: var(--rounded-card, 8px); margin-bottom: var(--sp-3, 1rem);">
                  <h4 class="heading font-bold title-md mb-4">${product.title}</h4>
                </div>

                ${this.buildVariantOptionsHTML()}

              </div>

              <div class="drawer__footer" style="padding: var(--sp-5) var(--sp-5) var(--sp-4); border-top: 1px solid rgb(var(--color-drawer-text) / 0.1); background-color: rgb(var(--color-drawer-background)); flex-shrink: 0;">
                <div class="flex gap-4 w-full">
                  <button
                    class="button button--secondary"
                    is="hover-button"
                    data-action="close"
                    style="flex: 1;">
                    <span class="btn-fill" data-fill></span>
                    <span class="btn-text">Cancel</span>
                  </button>
                  <button
                    class="button button--primary"
                    is="hover-button"
                    data-action="add-and-proceed"
                    style="flex: 1;">
                    <span class="btn-fill" data-fill></span>
                    <span class="btn-text">Add to Cart & Continue</span>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </x-modal>
      `;
    }

    /**
     * Build variant option selectors (colour, size, etc.)
     * @param {string} prefix - ID prefix for unique element IDs (e.g., 'new-variant')
     */
    buildVariantOptionsHTML(prefix = '') {
      const product = this.productData;

      // Group variants by option (e.g., Color, Size)
      const options = product.options || [];

      return options.map((option, optionIndex) => {
        if (option.name.toLowerCase() === 'title' && option.values.length === 1) {
          return ''; // Skip default "Title" option
        }

        const isColorOption = option.name.toLowerCase().includes('colour') ||
                              option.name.toLowerCase().includes('color');

        if (isColorOption) {
          return this.buildColorSwatchesHTML(option, optionIndex, prefix);
        } else {
          return this.buildDropdownHTML(option, optionIndex, prefix);
        }
      }).join('');
    }

    /**
     * Build colour swatches as grid cards (matches bundle drawer style)
     */
    buildColorSwatchesHTML(option, optionIndex, prefix = '') {
      const product = this.productData;
      const currentVariant = product.variants.find(v => v.id === this.selectedVariantId);
      const currentValue = currentVariant?.options[optionIndex];
      const containerId = prefix ? `${prefix}-colour-swatches-${optionIndex}` : `colour-swatches-${optionIndex}`;

      return `
        <div class="variant-option variant-option--swatches">
          <label class="variant-option__label">${option.name}:</label>
          <div class="colour-swatches-grid" id="${containerId}" data-option-index="${optionIndex}" data-prefix="${prefix}">
            ${option.values.map(value => {
              const isSelected = value === currentValue;
              const variantForColor = product.variants.find(v => v.options[optionIndex] === value && v.available);

              if (!variantForColor) return ''; // Skip unavailable colours

              return `
                <div class="swatch-card ${isSelected ? 'selected' : ''}"
                     data-value="${value}"
                     data-variant-id="${variantForColor.id}"
                     data-prefix="${prefix}"
                     role="button"
                     tabindex="0">
                  <div class="swatch-card__checkbox">
                    ${isSelected ? '<span class="swatch-card__checkbox-icon">✓</span>' : ''}
                  </div>
                  <div class="swatch-card__colour"
                       style="background-color: ${this.getColorHex(value)};"></div>
                  <span class="swatch-card__label">${value}</span>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }

    /**
     * Build dropdown for non-colour options
     */
    buildDropdownHTML(option, optionIndex, prefix = '') {
      const product = this.productData;
      const currentVariant = product.variants.find(v => v.id === this.selectedVariantId);
      const currentValue = currentVariant?.options[optionIndex];
      const selectId = prefix ? `${prefix}-select-${optionIndex}` : `select-${optionIndex}`;

      return `
        <div class="variant-option variant-option--dropdown">
          <label class="variant-option__label">${option.name}:</label>
          <select class="variant-option__select" id="${selectId}" data-option-index="${optionIndex}" data-prefix="${prefix}">
            ${option.values.map(value => `
              <option value="${value}" ${value === currentValue ? 'selected' : ''}>
                ${value}
              </option>
            `).join('')}
          </select>
        </div>
      `;
    }

    /**
     * Build cart item selector modal HTML
     */
    buildCartItemSelectorHTML(cartItems) {
      return `
        <x-modal id="product-linking-cart-selector"
                 class="x-modal drawer z-30 fixed bottom-0 left-0 h-full w-full pointer-events-none"
                 role="dialog"
                 aria-modal="true"
                 aria-label="Choose Cart Item"
                 hidden>

          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none"
                           aria-controls="product-linking-cart-selector"
                           aria-expanded="false"></overlay-element>

          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">

            <gesture-element class="drawer__header flex items-center justify-between opacity-0 invisible relative">
              <div>
                <h3 class="drawer__title heading font-bold title-md mb-1">Pick Your Ride!</h3>
                <p class="text-sm opacity-70">Choose which one gets the upgrade</p>
              </div>
              <button
                class="button button--secondary button--close drawer__close hidden sm:flex items-center justify-center"
                type="button"
                is="hover-button"
                data-action="close"
                aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
                  </svg>
                </span>
              </button>
            </gesture-element>

            <div class="drawer__content opacity-0 invisible flex flex-col h-full grow shrink">
              <div class="drawer__scrollable relative flex flex-col gap-5 md:gap-7 grow shrink p-5 md:p-12">
                <div class="cart-item-options flex flex-col gap-4">
                  ${cartItems.length > 0 ? `
                    <!-- Section: Already in Cart -->
                    <div style="margin-bottom: 1rem;">
                      <h4 style="font-size: 0.875rem; font-weight: 600; color: rgb(var(--color-drawer-text) / 0.6); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.75rem;">
                        Already in Your Cart
                      </h4>
                      <div class="flex flex-col gap-3">
                        ${cartItems.map((cartItem, index) => this.buildCartItemCardHTML(cartItem, index)).join('')}
                      </div>
                    </div>
                  ` : ''}

                  <!-- Section: Add New Product -->
                  <div>
                    <h4 style="font-size: 0.875rem; font-weight: 600; color: rgb(var(--color-drawer-text) / 0.6); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.75rem;">
                      ${cartItems.length > 0 ? 'Or Add a Different Option' : 'Select Your Options'}
                    </h4>
                    ${this.buildAddNewCardHTML()}
                  </div>
                </div>
              </div>

              <div class="drawer__footer" style="padding: var(--sp-5) var(--sp-5) var(--sp-4); border-top: 1px solid rgb(var(--color-drawer-text) / 0.1); background-color: rgb(var(--color-drawer-background)); flex-shrink: 0;">
                <div class="flex gap-4 w-full">
                  <button
                    class="button button--secondary"
                    is="hover-button"
                    data-action="close"
                    style="flex: 1;">
                    <span class="btn-fill" data-fill></span>
                    <span class="btn-text">Cancel</span>
                  </button>
                  <button
                    class="button button--primary"
                    is="hover-button"
                    data-action="confirm-selection"
                    style="flex: 1;">
                    <span class="btn-fill" data-fill></span>
                    <span class="btn-text">Continue</span>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </x-modal>
      `;
    }

    /**
     * Build individual cart item card (matches bundle drawer design)
     */
    buildCartItemCardHTML(cartItem, index) {
      const variant = this.productData.variants.find(v => v.id === cartItem.variant_id);
      const variantTitle = cartItem.variant_title !== 'Default Title' ? cartItem.variant_title : '';
      const isSelected = index === 0; // First item selected by default

      return `
        <div class="cart-item-card ${isSelected ? 'selected' : ''}"
             data-cart-key="${cartItem.key}"
             data-cart-index="${index}"
             data-selection="existing-${index}"
             role="button"
             tabindex="0">
          <div class="cart-item-card__content">
            <!-- Checkbox -->
            <div class="cart-item-card__checkbox">
              ${isSelected ? '<span class="cart-item-card__checkbox-icon">✓</span>' : ''}
            </div>

            <!-- Image -->
            <div class="cart-item-card__image">
              <img src="${cartItem.featured_image?.url || cartItem.image}"
                   alt="${cartItem.product_title}"
                   loading="lazy">
            </div>

            <!-- Details -->
            <div class="cart-item-card__details">
              <h4 class="cart-item-card__title">${cartItem.product_title}</h4>
              ${variantTitle ? `<p class="cart-item-card__variant">${variantTitle}</p>` : ''}
              <span class="cart-item-card__badge badge badge--success">In Cart</span>
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Build "Add New" card with two-column layout
     */
    buildAddNewCardHTML() {
      const product = this.productData;
      const firstVariant = product.variants[0];

      return `
        <div class="cart-item-card cart-item-card--new" data-expandable="true" data-selection="new">
          <!-- Collapsed Header (always visible) -->
          <div class="cart-item-card__header" data-action="toggle-new-variant">
            <div class="cart-item-card__content">
              <div class="cart-item-card__checkbox">
                <!-- Checkbox appears when selected -->
              </div>
              <div class="new-item-icon">
                <svg class="icon" width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14m-7-7h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="cart-item-card__details">
                <h4 class="cart-item-card__title">Add a New Product</h4>
                <p class="cart-item-card__variant">Select colour and options below</p>
              </div>
              <svg class="cart-item-card__chevron transition-transform" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Expanded Content (visible by default) - Two Column Layout -->
          <div class="cart-item-card__expandable" style="display: block;">
            <div class="cart-item-card__two-column">
              <!-- Column 1: Product Image and Name -->
              <div class="cart-item-card__product-column">
                <img src="${firstVariant.featured_image?.src || product.featured_image}"
                     alt="${product.title}"
                     id="new-variant-preview-image"
                     class="cart-item-card__product-image">
                <h5 class="cart-item-card__product-name">${product.title}</h5>
              </div>

              <!-- Column 2: Variant Options Grid -->
              <div class="cart-item-card__options-column">
                ${this.buildVariantOptionsHTML('new-variant')}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    /**
     * Inject modal into DOM
     */
    injectModal(html, modalId) {
      // Remove existing modal if present
      const existing = document.getElementById(modalId);
      if (existing) existing.remove();

      // Inject new modal
      document.body.insertAdjacentHTML('beforeend', html);
      this.modal = document.getElementById(modalId);
    }

    /**
     * Show modal (x-modal system)
     */
    showModal() {
      if (!this.modal) return;

      // Remove hidden attribute to show x-modal
      this.modal.removeAttribute('hidden');

      // Trigger x-modal's show method if available
      if (typeof this.modal.show === 'function') {
        this.modal.show();
      } else {
        // Fallback: Manually activate drawer
        requestAnimationFrame(() => {
          this.modal.setAttribute('open', '');

          requestAnimationFrame(() => {
            this.modal.setAttribute('active', '');
          });
        });
      }

      // NOTE: Do NOT manually add 'has-modal-open' class
      // The x-modal system manages this automatically via lockLayerCount

      // Trap focus for accessibility
      if (window.theme?.a11y?.trapFocus) {
        window.theme.a11y.trapFocus(this.modal);
      }
    }

    /**
     * Hide modal (x-modal system)
     * Returns a promise that resolves when modal is fully hidden
     */
    async hideModal() {
      if (!this.modal) return Promise.resolve();

      // Trigger x-modal's hide method and wait for it to complete
      if (typeof this.modal.hide === 'function') {
        await this.modal.hide();
      } else {
        // Fallback: Manually deactivate drawer
        this.modal.removeAttribute('active');

        await new Promise(resolve => {
          setTimeout(() => {
            if (this.modal) {
              this.modal.removeAttribute('open');
              this.modal.setAttribute('hidden', '');
            }
            resolve();
          }, 600); // Match drawer transition duration
        });
      }

      // NOTE: Do NOT manually remove 'has-modal-open' class or remove modal from DOM
      // The x-modal system manages scroll lock and cleanup automatically via lockLayerCount
    }

    /**
     * Attach event listeners for variant picker
     */
    attachVariantPickerListeners() {
      const modal = this.modal;
      if (!modal) return;

      // Close button
      modal.querySelectorAll('[data-action="close"]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.hideModal();
          this.onCancel();
        });
      });

      // Overlay click to close
      const overlay = modal.querySelector('overlay-element');
      if (overlay) {
        overlay.addEventListener('click', () => {
          this.hideModal();
          this.onCancel();
        });
      }

      // Colour swatch clicks
      modal.querySelectorAll('.swatch').forEach(swatch => {
        swatch.addEventListener('click', () => {
          const variantId = parseInt(swatch.dataset.variantId);
          this.selectedVariantId = variantId;

          // Update UI
          modal.querySelectorAll('.swatch').forEach(s => s.classList.remove('swatch--selected'));
          swatch.classList.add('swatch--selected');

          // Update product image
          const variant = this.productData.variants.find(v => v.id === variantId);
          if (variant?.featured_image) {
            const img = modal.querySelector('.product-preview__image');
            if (img) img.src = variant.featured_image.src;
          }
        });
      });

      // Dropdown changes
      modal.querySelectorAll('.variant-option__select').forEach(select => {
        select.addEventListener('change', (e) => {
          this.updateVariantSelection();
        });
      });

      // Add to cart button
      const addBtn = modal.querySelector('[data-action="add-and-proceed"]');
      if (addBtn) {
        addBtn.addEventListener('click', async () => {
          await this.addVariantToCartAndProceed();
        });
      }
    }

    /**
     * Attach event listeners for cart item selector (card-based selection)
     */
    attachCartItemSelectorListeners(cartItems) {
      const modal = this.modal;
      if (!modal) return;

      // Close button
      modal.querySelectorAll('[data-action="close"]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.hideModal();
          this.onCancel();
        });
      });

      // Overlay click to close
      const overlay = modal.querySelector('overlay-element');
      if (overlay) {
        overlay.addEventListener('click', () => {
          this.hideModal();
          this.onCancel();
        });
      }

      // Cart item card selection (excluding new card)
      modal.querySelectorAll('.cart-item-card:not(.cart-item-card--new)').forEach((card, index) => {
        card.addEventListener('click', () => {
          // Remove selected from all cards
          modal.querySelectorAll('.cart-item-card').forEach(c => {
            c.classList.remove('selected');
            const checkbox = c.querySelector('.cart-item-card__checkbox');
            if (checkbox) checkbox.innerHTML = '';
          });

          // Add selected to clicked card
          card.classList.add('selected');
          const checkbox = card.querySelector('.cart-item-card__checkbox');
          if (checkbox) {
            checkbox.innerHTML = '<span class="cart-item-card__checkbox-icon">✓</span>';
          }
        });
      });

      // Toggle "Add New" card expansion
      const toggleBtn = modal.querySelector('[data-action="toggle-new-variant"]');
      if (toggleBtn) {
        toggleBtn.addEventListener('click', (e) => {
          const card = modal.querySelector('.cart-item-card--new');
          const expandable = card.querySelector('.cart-item-card__expandable');
          const chevron = card.querySelector('.cart-item-card__chevron');

          const isExpanded = expandable.style.display !== 'none';

          if (isExpanded) {
            // Collapse
            expandable.style.display = 'none';
            chevron.style.transform = 'rotate(0deg)';
            card.classList.remove('selected');
            const checkbox = card.querySelector('.cart-item-card__checkbox');
            if (checkbox) checkbox.innerHTML = '';
          } else {
            // Expand and select
            expandable.style.display = 'block';
            chevron.style.transform = 'rotate(180deg)';

            // Deselect all other cards
            modal.querySelectorAll('.cart-item-card').forEach(c => {
              c.classList.remove('selected');
              const cb = c.querySelector('.cart-item-card__checkbox');
              if (cb) cb.innerHTML = '';
            });

            // Select this card
            card.classList.add('selected');
            const checkbox = card.querySelector('.cart-item-card__checkbox');
            if (checkbox) {
              checkbox.innerHTML = '<span class="cart-item-card__checkbox-icon">✓</span>';
            }
          }
        });
      }

      // Color swatch card clicks for "new variant" section
      modal.querySelectorAll('.swatch-card[data-prefix="new-variant"]').forEach(swatchCard => {
        swatchCard.addEventListener('click', () => {
          const variantId = parseInt(swatchCard.dataset.variantId);
          this.selectedNewVariantId = variantId;

          // Update UI - remove all selected states
          const prefix = swatchCard.dataset.prefix;
          modal.querySelectorAll(`.swatch-card[data-prefix="${prefix}"]`).forEach(sc => {
            sc.classList.remove('selected');
            const checkbox = sc.querySelector('.swatch-card__checkbox');
            if (checkbox) checkbox.innerHTML = '';
          });

          // Add selected state to clicked swatch card
          swatchCard.classList.add('selected');
          const checkbox = swatchCard.querySelector('.swatch-card__checkbox');
          if (checkbox) {
            checkbox.innerHTML = '<span class="swatch-card__checkbox-icon">✓</span>';
          }

          // Update preview image
          const variant = this.productData.variants.find(v => v.id === variantId);
          if (variant?.featured_image) {
            const img = modal.querySelector('#new-variant-preview-image');
            if (img) img.src = variant.featured_image.src;
          }

          // Ensure "Add New" card is selected and expanded
          const newCard = modal.querySelector('.cart-item-card--new');
          if (newCard && !newCard.classList.contains('selected')) {
            const toggleButton = modal.querySelector('[data-action="toggle-new-variant"]');
            if (toggleButton) toggleButton.click();
          }
        });
      });

      // Dropdown changes for "new variant" section
      modal.querySelectorAll('.variant-option__select[data-prefix="new-variant"]').forEach(select => {
        select.addEventListener('change', () => {
          this.updateNewVariantSelection(modal);

          // Ensure "Add New" card is selected
          const newCard = modal.querySelector('.cart-item-card--new');
          if (newCard && !newCard.classList.contains('selected')) {
            const toggleButton = modal.querySelector('[data-action="toggle-new-variant"]');
            if (toggleButton) toggleButton.click();
          }
        });
      });

      // Confirm selection button
      const confirmBtn = modal.querySelector('[data-action="confirm-selection"]');
      if (confirmBtn) {
        confirmBtn.addEventListener('click', async () => {
          const selectedCard = modal.querySelector('.cart-item-card.selected');
          if (!selectedCard) return;

          const selection = selectedCard.dataset.selection;

          if (selection === 'new') {
            // User wants to add new variant (from expanded card)
            await this.addNewVariantAndProceed();
          } else {
            // User selected existing cart item
            const index = parseInt(selectedCard.dataset.cartIndex);
            const cartItem = cartItems[index];

            await this.hideModal();
            await this.handleSingleCartItem(cartItem);
          }
        });
      }
    }

    /**
     * Update variant selection based on dropdown changes
     */
    updateVariantSelection() {
      const modal = this.modal;
      if (!modal) return;

      // Get selected option values (excluding new-variant prefix)
      const selectedOptions = Array.from(modal.querySelectorAll('.variant-option__select:not([data-prefix="new-variant"])')).map(select => select.value);

      // Find matching variant
      const variant = this.productData.variants.find(v => {
        return selectedOptions.every((value, index) => v.options[index] === value);
      });

      if (variant) {
        this.selectedVariantId = variant.id;

        // Update product image
        if (variant.featured_image) {
          const img = modal.querySelector('.product-preview__image');
          if (img) img.src = variant.featured_image.src;
        }
      }
    }

    /**
     * Update new variant selection based on dropdown changes in "Add New" section
     */
    updateNewVariantSelection(modal) {
      // Get selected option values from new-variant section
      const selectedOptions = Array.from(modal.querySelectorAll('.variant-option__select[data-prefix="new-variant"]')).map(select => select.value);

      // Find matching variant
      const variant = this.productData.variants.find(v => {
        return selectedOptions.every((value, index) => v.options[index] === value);
      });

      if (variant) {
        this.selectedNewVariantId = variant.id;

        // Update preview image
        if (variant.featured_image) {
          const img = modal.querySelector('#new-variant-preview-image');
          if (img) img.src = variant.featured_image.src;
        }
      }
    }

    /**
     * Add selected variant to cart and proceed (from variant picker modal)
     */
    async addVariantToCartAndProceed() {
      const modal = this.modal;
      const addBtn = modal.querySelector('[data-action="add-and-proceed"]');

      if (!this.selectedVariantId) {
        alert('Please select a variant');
        return;
      }

      try {
        // Update button state
        addBtn.disabled = true;
        addBtn.querySelector('.btn-text').textContent = 'Adding to Cart...';

        // Generate productLinkId
        const productLinkId = `${this.productId}-${Date.now()}`;

        // Add to cart with productLinkId
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.selectedVariantId,
            quantity: 1,
            properties: {
              productLinkId: productLinkId
            }
          })
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        const cartItem = await response.json();

        // Logging removed

        // Refresh cart drawer with sections update
        // Logging removed
        await this.refreshCartDrawer();
        // Logging removed

        // Hide modal and wait for it to fully close
        await this.hideModal();

        // Trigger success callback
        this.onReady(productLinkId, cartItem);

      } catch (error) {
        console.error('[PLM] Error adding to cart:', error);
        addBtn.disabled = false;
        addBtn.querySelector('.btn-text').textContent = 'Add to Cart & Continue';
        alert('Failed to add to cart. Please try again.');
        this.onError(error);
      }
    }

    /**
     * Add new variant to cart from expanded "Add New" card
     */
    async addNewVariantAndProceed() {
      const modal = this.modal;
      const confirmBtn = modal.querySelector('[data-action="confirm-selection"]');

      if (!this.selectedNewVariantId) {
        alert('Please select a variant');
        return;
      }

      // Logging removed
      // Logging removed

      try {
        // Update button state
        confirmBtn.disabled = true;
        const originalText = confirmBtn.querySelector('.btn-text').textContent;
        confirmBtn.querySelector('.btn-text').textContent = 'Adding to Cart...';

        // Generate productLinkId
        const productLinkId = `${this.productId}-${Date.now()}`;
        // Logging removed

        // Add to cart with productLinkId
        // Logging removed
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.selectedNewVariantId,
            quantity: 1,
            properties: {
              productLinkId: productLinkId
            }
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[PLM] Add to cart failed:', response.status, errorText);
          throw new Error(`Failed to add to cart: ${response.status}`);
        }

        const cartItem = await response.json();
        // Logging removed

        // Refresh cart drawer with sections update (proper way)
        // Logging removed
        await this.refreshCartDrawer();
        // Logging removed

        // Hide modal and wait for it to fully close
        // Logging removed
        await this.hideModal();
        // Logging removed

        // Trigger success callback
        // Logging removed
        this.onReady(productLinkId, cartItem);
        // Logging removed

      } catch (error) {
        console.error('[PLM] ERROR in addNewVariantAndProceed:', error);
        console.error('[PLM] Error stack:', error.stack);
        confirmBtn.disabled = false;
        confirmBtn.querySelector('.btn-text').textContent = originalText;
        alert('Failed to add to cart. Please try again.');
        this.onError(error);
      }
    }

    /**
     * Get hex colour from colour name (basic mapping)
     */
    getColorHex(colorName) {
      const colorMap = {
        'red': '#ff0000',
        'blue': '#0000ff',
        'green': '#00ff00',
        'black': '#000000',
        'white': '#ffffff',
        'pink': '#ff69b4',
        'yellow': '#ffff00',
        'orange': '#ffa500',
        'purple': '#800080',
        'grey': '#808080',
        'gray': '#808080',
        'brown': '#8b4513',
        'navy': '#000080',
        'teal': '#008080'
      };

      const normalised = colorName.toLowerCase().trim();
      return colorMap[normalised] || '#cccccc';
    }
  }
}
